import React, { useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { formatPhoneNumber } from '@/utils/helpers';
import Modal from '../Modals/Modal';

const formSchema = z.object({
  zip_code: z.string().min(1, { message: 'You must enter a zip code' }),
});
type PhoneNumber = {
  phoneNumber: string;
  locality: string;
  region: string;
  country: string;
  capabilities: string[];
};

const dummyPhoneNumbers: PhoneNumber[] = [
  {
    phoneNumber: '+1234567890',
    locality: 'New York',
    region: 'NY',
    country: 'US',
    capabilities: ['voice', 'sms'],
  },
  {
    phoneNumber: '+1987654321',
    locality: 'Los Angeles',
    region: 'CA',
    country: 'US',
    capabilities: ['voice', 'sms', 'mms'],
  },
  {
    phoneNumber: '+1567890123',
    locality: 'Chicago',
    region: 'IL',
    country: 'US',
    capabilities: ['voice'],
  },
];

const PurchasePhoneNumberModal = NiceModal.create(() => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      zip_code: '',
    },
  });

  const modal = useModal('purchase-phone-number-modal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [purchaseStatus, setPurchaseStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const { handleSubmit } = form;

  async function onSubmit(data: z.infer<typeof formSchema>) {
    console.log(data);
    setIsSubmitting(true);
    try {
      // Simulate API call delay
      setTimeout(() => {
        setPhoneNumbers(dummyPhoneNumbers);
        setIsSubmitting(false);
      }, 1000);
    } catch (error) {
      console.error('Error searching numbers:', error);
      setIsSubmitting(false);
    }
  }

  const handlePurchase = async (phoneNumber: string) => {
    console.log('Purchasing phone number:', phoneNumber);
    // Simulate purchase success
    setPurchaseStatus({
      success: true,
      message: `Successfully purchased ${phoneNumber}`,
    });
  };

  return (
    <Modal modal={modal} title="Purchase Phone Number">
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="flex items-end gap-2">
            <FormField
              control={form.control}
              name="zip_code"
              render={({ field }) => (
                <div className="w-full">
                  <FormItem>
                    <FormLabel>Zip Code</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter a zip code" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </form>
      </Form>

      {phoneNumbers.length > 0 && (
        <>
          <h3 className="text-lg font-medium">Available Numbers:</h3>

          <div className="mt-4 space-y-4 overflow-y-auto max-h-64">
            {phoneNumbers.map((number, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-md">
                <div>
                  <p className="text-xl font-bold">{formatPhoneNumber(number.phoneNumber)}</p>
                  <p className="text-sm text-gray-600">
                    {number.locality}, {number.region}, {number.country}
                  </p>
                  <p className="text-sm">
                    Capabilities: <span className="font-medium">{number.capabilities.join(', ')}</span>
                  </p>
                </div>
                <Button onClick={() => handlePurchase(number.phoneNumber)}>Purchase</Button>
              </div>
            ))}
          </div>
        </>
      )}

      {purchaseStatus && (
        <div className={`mt-4 p-4 rounded-md ${purchaseStatus.success ? 'bg-green-100' : 'bg-red-100'}`}>
          {purchaseStatus.message}
        </div>
      )}
    </Modal>
  );
});

export default PurchasePhoneNumberModal;
